import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from '../SystemHeader';
import { F1API } from '../../services/feature-api';
import NutZalo from '../../utils/NutZalo';

// QT Quân Số (STT 3.1) — quân số + người mới của TOÀN NHÁNH (F1, F2, F3...), khác /f1 chỉ hiện F1 trực tiếp.
// Số liệu lấy từ view user_yody trên DB qua endpoint /f1-management/quan-so.

const RANGES = [
  { days: 10, label: '10 ngày' },
  { days: 30, label: '30 ngày' },
  { days: 90, label: '3 tháng' },
];

const CHART_HEIGHT = 96;

const fmtInt = (n) => (Number(n) || 0).toLocaleString('vi-VN');

// '2026-08-05' -> '05/08'
const fmtDayMonth = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

const QuanSoPage = ({ accessToken, userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const chartScrollRef = useRef(null);

  const [days, setDays] = useState(10);
  const [selectedDate, setSelectedDate] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError('');
    try {
      const result = await F1API.getQuanSo(accessToken, { days, date: selectedDate });
      if (result.success) setData(result.data || null);
      else setError(result.error || 'Lỗi tải dữ liệu');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, days, selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Biểu đồ 30/90 ngày dài hơn màn hình → tự kéo tới ngày gần nhất (bên phải).
  useEffect(() => {
    const el = chartScrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [data?.rangeDays]);

  const chart = data?.chart || [];
  const members = data?.members || [];
  const maxCount = chart.reduce((m, c) => Math.max(m, Number(c.count) || 0), 0);

  const rangeLabel = RANGES.find(r => r.days === days)?.label || `${days} ngày`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-8">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="QT Quân Số"
        onRefreshPoints={onRefreshPoints}
      />

      <div className="flex items-center gap-3 px-3 py-2 bg-white border-b border-gray-200">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors text-cyan-700 bg-cyan-50 hover:bg-cyan-100"
        >
          <span className="material-symbols-rounded" style={{ fontSize: '14px' }}>arrow_back</span>
          <span>Quay lại</span>
        </button>
        <h1 className="text-sm font-semibold text-cyan-900 flex-1">QT Quân Số</h1>
      </div>

      <div className="px-3 pt-3 space-y-3">
        {/* Tổng quân số — thông tin nổi bật nhất trang */}
        <div className="rounded-2xl px-4 py-4 text-white shadow-md"
             style={{ background: 'linear-gradient(135deg, #0E7490, #06B6D4)' }}>
          <p className="text-white/80 text-[11px] font-semibold uppercase tracking-wider">Tổng quân số toàn nhánh</p>
          <p className="text-[44px] leading-none font-black mt-1">
            {loading && !data ? '...' : fmtInt(data?.totalMembers)}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="bg-white/20 rounded-full px-2.5 py-1 text-[12px] font-bold">
              +{fmtInt(data?.newInRange)} người mới / {rangeLabel}
            </span>
          </div>
        </div>

        {/* Chọn khoảng thời gian */}
        <div className="flex gap-2">
          {RANGES.map(r => (
            <button key={r.days}
                    onClick={() => { setDays(r.days); setSelectedDate(null); }}
                    className={`flex-1 py-2 rounded-xl text-[13px] font-bold border transition-colors ${
                      days === r.days
                        ? 'bg-cyan-600 text-white border-cyan-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-300'
                    }`}>
              {r.label}
            </button>
          ))}
        </div>

        {/* Lỗi API hiện bằng tiếng người trước (người dùng là đại lý), chi tiết kỹ thuật để nhỏ bên dưới */}
        {error && (
          <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-[13px] font-semibold">Chưa lấy được số liệu quân số. Vui lòng thử lại sau ít phút.</p>
            <p className="text-red-400 text-[10px] mt-0.5">{error}</p>
          </div>
        )}

        {/* Biểu đồ cột: 1 cột = 1 ngày, bấm vào cột để xem danh sách người vào đúng ngày đó */}
        <div className="bg-white rounded-2xl border border-gray-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] font-bold text-gray-700">Người mới theo ngày</p>
            <p className="text-[10px] text-gray-400">Bấm vào cột để lọc</p>
          </div>
          {/* overflow-y-hidden: chỉ cho cuộn ngang, nếu không trình duyệt tự bật thêm thanh cuộn dọc thừa */}
          <div ref={chartScrollRef} className="overflow-x-auto overflow-y-hidden">
            <div className="flex items-end gap-1" style={{ minWidth: '100%' }}>
              {chart.map(c => {
                const n = Number(c.count) || 0;
                const isSel = selectedDate === c.date;
                const h = maxCount > 0 ? Math.round((n / maxCount) * CHART_HEIGHT) : 0;
                return (
                  <button key={c.date}
                          onClick={() => setSelectedDate(isSel ? null : c.date)}
                          className="flex-1 flex flex-col items-center justify-end"
                          style={{ minWidth: '26px' }}>
                    <span className={`text-[10px] font-bold mb-0.5 ${n > 0 ? 'text-cyan-800' : 'text-gray-300'}`}>{n || ''}</span>
                    <div className={`w-full rounded-t-md transition-colors ${
                        isSel ? 'bg-amber-500' : n > 0 ? 'bg-cyan-500' : 'bg-gray-200'
                      }`}
                      style={{ height: `${Math.max(h, n > 0 ? 6 : 3)}px` }} />
                    <span className={`text-[9px] mt-1 leading-tight ${isSel ? 'text-amber-700 font-bold' : 'text-gray-500'}`}>
                      {c.date.slice(8, 10)}
                    </span>
                    <span className="text-[8px] text-gray-400 leading-tight">
                      {c.date.slice(8, 10) === '01' ? `T${Number(c.date.slice(5, 7))}` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Danh sách người mới — mặc định cả kỳ, bấm cột thì chỉ ngày đó */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-cyan-50 border-b border-cyan-100">
            <p className="text-[12px] font-bold text-cyan-900">
              {selectedDate ? `Người mới ngày ${fmtDayMonth(selectedDate)}` : `Người mới ${rangeLabel} gần nhất`}
              <span className="ml-1 font-semibold text-cyan-700">· {fmtInt(members.length)} người</span>
            </p>
            {selectedDate && (
              <button onClick={() => setSelectedDate(null)}
                      className="text-[11px] font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
                Xem tất cả
              </button>
            )}
          </div>

          {loading ? (
            <div className="py-6 text-center text-gray-400 text-xs">Đang tải...</div>
          ) : members.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">Chưa có người mới trong kỳ này.</div>
          ) : (
            <div>
              {members.map((m, idx) => (
                <div key={`${m.sdt}-${idx}`}
                     className={`px-3 py-2 border-b border-gray-100 last:border-0 ${idx % 2 ? 'bg-gray-50' : 'bg-white'}`}>
                  {/* Hàng 1: định danh + SĐT + ngày vào */}
                  <div className="flex items-center gap-2">
                    <p className="text-[13px] font-bold text-blue-900 flex-1 truncate">{m.codeFacebook}</p>
                    <NutZalo sdt={m.sdt} />
                    <span className="text-[10px] text-white bg-cyan-600 rounded-full px-1.5 py-0.5 font-bold">
                      {fmtDayMonth(m.joinDate)}
                    </span>
                  </div>
                  {/* Hàng 2: tuyến trên (f_logic) — dài nên cho xuống tối đa 2 dòng thay vì cắt cụt 1 dòng */}
                  <p className="text-[10.5px] text-gray-500 leading-snug mt-0.5"
                     style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {m.fLogic || '—'}
                  </p>
                </div>
              ))}
              {data?.membersTruncated && (
                <p className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50">
                  Chỉ hiện 500 người gần nhất — chọn khoảng ngắn hơn để xem đủ.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuanSoPage;
